import React, { useEffect, useRef, useState } from 'react'
import './styles.scss'

export type MultiSelectOption = {
  label: string
  value: string
}

type MultiSelectProps = {
  options: MultiSelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  className?: string
  'aria-label'?: string
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
  'aria-label': ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  const selectedOptions = options.filter((option) =>
    value.includes(option.value)
  )

  const toggleOpen = () => {
    setIsOpen((prev) => !prev)
  }

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((item) => item !== optionValue))
      return
    }

    onChange([...value, optionValue])
  }

  const removeOption = (
    event: React.MouseEvent | React.KeyboardEvent,
    optionValue: string
  ): void => {
    event.stopPropagation()
    onChange(value.filter((item) => item !== optionValue))
  }

  const clearAll = (event: React.MouseEvent): void => {
    event.stopPropagation()
    onChange([])
  }

  return (
    <div
      ref={containerRef}
      className={`multi-select ${isOpen ? 'open' : ''} ${className}`.trim()}
    >
      <div
        className="multi-select-control"
        role="combobox"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        onClick={toggleOpen}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) {
            return
          }

          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            toggleOpen()
          }
        }}
      >
        <div className="multi-select-values">
          {selectedOptions.length === 0 ? (
            <span className="multi-select-placeholder">{placeholder}</span>
          ) : (
            selectedOptions.map((option) => (
              <span key={option.value} className="multi-select-pill">
                {option.label}
                <button
                  type="button"
                  className="multi-select-pill-remove"
                  aria-label={`Remove ${option.label}`}
                  onClick={(event) => removeOption(event, option.value)}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
        <div className="multi-select-actions">
          {selectedOptions.length > 0 && (
            <button
              type="button"
              className="multi-select-clear"
              aria-label="Clear all selected options"
              onClick={clearAll}
            >
              ×
            </button>
          )}
          <span className="multi-select-chevron" aria-hidden="true" />
        </div>
      </div>

      {isOpen && (
        <ul className="multi-select-menu" role="listbox" aria-multiselectable>
          {options.map((option) => {
            const isSelected = value.includes(option.value)

            return (
              <li key={option.value} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  className={`multi-select-option ${
                    isSelected ? 'selected' : ''
                  }`}
                  onClick={() => toggleOption(option.value)}
                >
                  <span
                    className="multi-select-option-check"
                    aria-hidden="true"
                  >
                    {isSelected && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2.5 6.5 5 9l4.5-5.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="square"
                          strokeLinejoin="miter"
                        />
                      </svg>
                    )}
                  </span>
                  {option.label}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default MultiSelect
